/**
 * Database Service using Dexie.js (IndexedDB)
 * Manages local anime database with full AniList catalog + user data
 */

import Dexie from 'dexie';

class DatabaseService extends Dexie {
    constructor() {
        super('AnitraktDB');

        // Define database schema - Version 1 (Anime)
        this.version(1).stores({
            anime: 'id, title_romaji, title_english, status, format, season_year, popularity',
            user_anime: 'anime_id, status, progress, updated_at',
            anime_relations: '++id, source_id, related_id, relation_type',
            sync_status: 'sync_type, last_sync_time',
            // New tables for user data persistence
            user_settings: 'key, value',
            anime_groups: '++id, timestamp',
            relations_cache: 'cache_key, data, timestamp'
        });

        // Version 2 - Add Movie tables
        this.version(2).stores({
            anime: 'id, title_romaji, title_english, status, format, season_year, popularity',
            user_anime: 'anime_id, status, progress, updated_at',
            anime_relations: '++id, source_id, related_id, relation_type',
            sync_status: 'sync_type, last_sync_time',
            user_settings: 'key, value',
            anime_groups: '++id, timestamp',
            relations_cache: 'cache_key, data, timestamp',
            // Movie tables
            movies: 'id, title, year, runtime, status',
            user_movies: 'movie_id, status, updated_at'
        });

        // Version 3 - Add TV Show tables
        this.version(3).stores({
            anime: 'id, title_romaji, title_english, status, format, season_year, popularity',
            user_anime: 'anime_id, status, progress, updated_at',
            anime_relations: '++id, source_id, related_id, relation_type',
            sync_status: 'sync_type, last_sync_time',
            user_settings: 'key, value',
            anime_groups: '++id, timestamp',
            relations_cache: 'cache_key, data, timestamp',
            // Movie tables
            movies: 'id, title, year, runtime, status',
            user_movies: 'movie_id, status, updated_at',
            // TV Show tables
            tv_shows: 'id, title, year, total_episodes, status',
            user_tv_shows: 'show_id, status, watched_episodes_count, updated_at'
        });

        // Version 4 - Add dropped-related anime and removed anime tables
        this.version(4).stores({
            anime: 'id, title_romaji, title_english, status, format, season_year, popularity',
            user_anime: 'anime_id, status, progress, updated_at',
            anime_relations: '++id, source_id, related_id, relation_type',
            sync_status: 'sync_type, last_sync_time',
            user_settings: 'key, value',
            anime_groups: '++id, timestamp',
            relations_cache: 'cache_key, data, timestamp',
            movies: 'id, title, year, runtime, status',
            user_movies: 'movie_id, status, updated_at',
            tv_shows: 'id, title, year, total_episodes, status',
            user_tv_shows: 'show_id, status, watched_episodes_count, updated_at',
            // Dropped-related anime (related to dropped-only groups, stored but hidden)
            dropped_related_anime: 'anime_id, added_at',
            // User-removed anime (replaces localStorage)
            removed_anime: 'anime_id, removed_at'
        });

        // Version 5 - Add compound index [source_id+related_id] on anime_relations for faster lookups
        this.version(5).stores({
            anime: 'id, title_romaji, title_english, status, format, season_year, popularity',
            user_anime: 'anime_id, status, progress, updated_at',
            anime_relations: '++id, source_id, related_id, relation_type, [source_id+related_id]',
            sync_status: 'sync_type, last_sync_time',
            user_settings: 'key, value',
            anime_groups: '++id, timestamp',
            relations_cache: 'cache_key, data, timestamp',
            movies: 'id, title, year, runtime, status',
            user_movies: 'movie_id, status, updated_at',
            tv_shows: 'id, title, year, total_episodes, status',
            user_tv_shows: 'show_id, status, watched_episodes_count, updated_at',
            dropped_related_anime: 'anime_id, added_at',
            removed_anime: 'anime_id, removed_at'
        });

        // Version 6 - Add large_cache table for data too large for localStorage (e.g., userData)
        this.version(6).stores({
            anime: 'id, title_romaji, title_english, status, format, season_year, popularity',
            user_anime: 'anime_id, status, progress, updated_at',
            anime_relations: '++id, source_id, related_id, relation_type, [source_id+related_id]',
            sync_status: 'sync_type, last_sync_time',
            user_settings: 'key, value',
            anime_groups: '++id, timestamp',
            relations_cache: 'cache_key, data, timestamp',
            movies: 'id, title, year, runtime, status',
            user_movies: 'movie_id, status, updated_at',
            tv_shows: 'id, title, year, total_episodes, status',
            user_tv_shows: 'show_id, status, watched_episodes_count, updated_at',
            dropped_related_anime: 'anime_id, added_at',
            removed_anime: 'anime_id, removed_at',
            large_cache: 'key'
        });

        // Version 7 - Remove legacy tables (catalog now lives on server, placeholder tables never used)
        this.version(7).stores({
            anime: null,              // Catalog now on server SQLite
            anime_relations: null,    // Relations now on server SQLite
            relations_cache: null,    // Cached AniList relation responses (server handles this)
            movies: null,             // Placeholder - never used
            user_movies: null,        // Placeholder - never used
            tv_shows: null,           // Placeholder - never used
            user_tv_shows: null,      // Placeholder - never used
        });

        // Table references (only active tables)
        this.user_anime = this.table('user_anime');
        this.sync_status = this.table('sync_status');
        this.user_settings = this.table('user_settings');
        this.anime_groups = this.table('anime_groups');
        this.dropped_related_anime = this.table('dropped_related_anime');
        this.removed_anime = this.table('removed_anime');
        this.large_cache = this.table('large_cache');

        this.isInitialized = false;
    }

    /**
     * Initialize the database
     * @returns {Promise<boolean>} Success status
     */
    async initialize() {
        try {
            await this.open();
            this.isInitialized = true;
            console.log('âœ… IndexedDB initialized');
            return true;
        } catch (error) {
            console.error('âŒ Failed to initialize database:', error);
            return false;
        }
    }

    // ========== USER ANIME METHODS ==========

    /**
     * Update user's anime data (progress, score, etc.)
     * @param {number} animeId Anime ID
     * @param {Object} userData User data
     */
    async updateUserAnime(animeId, userData) {
        try {
            await this.user_anime.put({
                anime_id: animeId,
                status: userData.status || null,
                progress: userData.progress || 0,
                score: userData.score || null,
                notes: userData.notes || null,
                is_removed: userData.isRemoved ? 1 : 0,
                started_at: userData.startedAt || null,
                completed_at: userData.completedAt || null,
                updated_at: Date.now()
            });
        } catch (error) {
            console.error('Error updating user anime:', error);
        }
    }



    /**
     * Update sync status
     * @param {string} syncType Sync type ('full_catalog' or 'user_list')
     * @param {Object} status Status object
     */
    async updateSyncStatus(syncType, status) {
        try {
            await this.sync_status.put({
                sync_type: syncType,
                last_sync_time: status.lastSyncTime || Date.now(),
                last_max_anime_id: status.lastMaxAnimeId || null,
                status: status.status || 'success',
                error_message: status.errorMessage || null
            });
        } catch (error) {
            console.error('Error updating sync status:', error);
        }
    }

    /**
     * Get sync status
     * @param {string} syncType Sync type
     * @returns {Promise<Object|null>} Sync status
     */
    async getSyncStatus(syncType) {
        try {
            return await this.sync_status.get(syncType);
        } catch (error) {
            console.error('Error getting sync status:', error);
            return null;
        }
    }

    /**
     * Get database statistics
     * @returns {Promise<Object>} Database stats
     */
    async getStats() {
        try {
            const userAnimeCount = await this.user_anime.count();
            const lastSync = await this.getSyncStatus('full_catalog');
            const lastUserSync = await this.getSyncStatus('user_list');

            return {
                totalAnime: 0, // Anime catalog now on server
                userAnimeCount,
                lastSync,
                lastUserSync
            };
        } catch (error) {
            console.error('Error getting stats:', error);
            return { totalAnime: 0, userAnimeCount: 0, lastSync: null, lastUserSync: null };
        }
    }

    /**
     * Clear all data (for debugging/reset)
     */
    async clearAllData() {
        try {
            await this.user_anime.clear();
            await this.sync_status.clear();
            await this.user_settings.clear();
            await this.anime_groups.clear();
            await this.dropped_related_anime.clear();
            await this.removed_anime.clear();
            await this.large_cache.clear();
            console.log('ðŸ—‘ï¸ All data cleared');
        } catch (error) {
            console.error('Error clearing data:', error);
        }
    }

    /**
     * Export database (for backup)
     * @returns {Promise<Object>} Database export
     */
    async exportDatabase() {
        try {
            const data = {
                user_anime: await this.user_anime.toArray(),
                sync_status: await this.sync_status.toArray(),
                user_settings: await this.user_settings.toArray(),
                anime_groups: await this.anime_groups.toArray(),
                dropped_related_anime: await this.dropped_related_anime.toArray(),
                removed_anime: await this.removed_anime.toArray(),
                large_cache: await this.large_cache.toArray()
            };
            return data;
        } catch (error) {
            console.error('Error exporting database:', error);
            return null;
        }
    }

    /**
     * Import database from backup
     * @param {Object} data Database export data
     */
    async importDatabase(data) {
        try {
            await this.clearAllData();

            if (data.user_anime) await this.user_anime.bulkPut(data.user_anime);
            if (data.sync_status) await this.sync_status.bulkPut(data.sync_status);
            if (data.user_settings) await this.user_settings.bulkPut(data.user_settings);
            if (data.anime_groups) await this.anime_groups.bulkPut(data.anime_groups);
            if (data.dropped_related_anime) await this.dropped_related_anime.bulkPut(data.dropped_related_anime);
            if (data.removed_anime) await this.removed_anime.bulkPut(data.removed_anime);
            if (data.large_cache) await this.large_cache.bulkPut(data.large_cache);

            console.log('âœ… Database imported');
        } catch (error) {
            console.error('Error importing database:', error);
            throw error;
        }
    }

    // ========== USER DATA METHODS (for localStorage replacement) ==========

    /**
     * Save user setting (replaces localStorage.setItem)
     * @param {string} key Setting key
     * @param {any} value Setting value
     */
    async saveUserSetting(key, value) {
        try {
            await this.user_settings.put({
                key,
                value: JSON.stringify(value),
                timestamp: Date.now()
            });
        } catch (error) {
            console.error(`Error saving setting ${key}:`, error);
        }
    }

    /**
     * Get user setting (replaces localStorage.getItem)
     * @param {string} key Setting key
     * @returns {Promise<any>} Setting value or null
     */
    async getUserSetting(key) {
        try {
            const setting = await this.user_settings.get(key);
            return setting ? JSON.parse(setting.value) : null;
        } catch (error) {
            console.error(`Error getting setting ${key}:`, error);
            return null;
        }
    }

    /**
     * Remove user setting (replaces localStorage.removeItem)
     * @param {string} key Setting key
     */
    async removeUserSetting(key) {
        try {
            await this.user_settings.delete(key);
        } catch (error) {
            console.error(`Error removing setting ${key}:`, error);
        }
    }

    // ========== LARGE CACHE METHODS (for data too large for localStorage) ==========

    /**
     * Save large data to IndexedDB cache (replaces localStorage for large payloads)
     * @param {string} key Cache key
     * @param {*} data Data to store
     * @param {number} ttl Time to live in milliseconds
     */
    async saveLargeCache(key, data, ttl) {
        try {
            await this.large_cache.put({
                key,
                data,
                timestamp: Date.now(),
                expiresAt: Date.now() + ttl
            });
        } catch (error) {
            console.error(`Error saving large cache ${key}:`, error);
        }
    }

    /**
     * Get large data from IndexedDB cache if not expired
     * @param {string} key Cache key
     * @returns {Promise<*>} Data if valid, null otherwise
     */
    async getLargeCache(key) {
        try {
            const cached = await this.large_cache.get(key);
            if (!cached) return null;

            // Check if expired
            if (Date.now() > cached.expiresAt) {
                await this.large_cache.delete(key);
                return null;
            }

            return cached.data;
        } catch (error) {
            console.error(`Error getting large cache ${key}:`, error);
            return null;
        }
    }

    /**
     * Check if large cache exists and is valid
     * @param {string} key Cache key
     * @returns {Promise<boolean>} True if cache is valid
     */
    async isLargeCacheValid(key) {
        try {
            const cached = await this.large_cache.get(key);
            if (!cached) return false;
            return Date.now() <= cached.expiresAt;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get large cache age in milliseconds
     * @param {string} key Cache key
     * @returns {Promise<number>} Age in ms, -1 if not found
     */
    async getLargeCacheAge(key) {
        try {
            const cached = await this.large_cache.get(key);
            if (!cached) return -1;
            return Date.now() - cached.timestamp;
        } catch (error) {
            return -1;
        }
    }

    /**
     * Clear a specific large cache entry
     * @param {string} key Cache key
     */
    async clearLargeCache(key) {
        try {
            await this.large_cache.delete(key);
        } catch (error) {
            console.error(`Error clearing large cache ${key}:`, error);
        }
    }

    /**
     * Save anime groups cache
     * @param {Object} groupsData Groups data (relations, groups, pending, etc.)
     */
    async saveGroupsCache(groupsData) {
        try {
            // Clear old groups
            await this.anime_groups.clear();

            // Save new groups data
            await this.anime_groups.add({
                relations: groupsData.relations,
                groups: groupsData.groups,
                pending: groupsData.pending,
                anime_count: groupsData.animeCount,
                cache_version: groupsData.cache_version,
                timestamp: groupsData.timestamp || Date.now()
            });

            console.log(`ðŸ’¾ Saved groups cache: ${groupsData.groups?.length} groups, ${groupsData.animeCount} anime`);
        } catch (error) {
            console.error('Error saving groups cache:', error);
        }
    }

    /**
     * Load anime groups cache
     * @returns {Promise<Object|null>} Groups cache or null
     */
    async loadGroupsCache() {
        try {
            const caches = await this.anime_groups
                .orderBy('timestamp')
                .reverse()
                .limit(1)
                .toArray();

            return caches.length > 0 ? caches[0] : null;
        } catch (error) {
            console.error('Error loading groups cache:', error);
            return null;
        }
    }

    // ========== DROPPED-RELATED ANIME METHODS ==========

    /**
     * Add anime to dropped-related list (found via relation to dropped-only groups)
     * @param {number} animeId Anime ID
     * @returns {Promise<boolean>} Success status
     */
    async addDroppedRelatedAnime(animeId) {
        try {
            const existing = await this.dropped_related_anime.get(animeId);
            if (!existing) {
                await this.dropped_related_anime.put({
                    anime_id: animeId,
                    added_at: Date.now()
                });
            }
            return true;
        } catch (error) {
            console.error('Error adding dropped-related anime:', error);
            return false;
        }
    }

    /**
     * Batch add multiple anime to dropped-related list
     * @param {Array<number>} animeIds Array of anime IDs
     * @returns {Promise<boolean>} Success status
     */
    async batchAddDroppedRelatedAnime(animeIds) {
        try {
            const data = animeIds.map(id => ({
                anime_id: id,
                added_at: Date.now()
            }));
            await this.dropped_related_anime.bulkPut(data);
            return true;
        } catch (error) {
            console.error('Error batch adding dropped-related anime:', error);
            return false;
        }
    }

    /**
     * Get all dropped-related anime IDs
     * @returns {Promise<Set<number>>} Set of anime IDs
     */
    async getDroppedRelatedAnimeIds() {
        try {
            const items = await this.dropped_related_anime.toArray();
            return new Set(items.map(item => item.anime_id));
        } catch (error) {
            console.error('Error getting dropped-related anime IDs:', error);
            return new Set();
        }
    }

    /**
     * Check if anime is in dropped-related list
     * @param {number} animeId Anime ID
     * @returns {Promise<boolean>} True if in list
     */
    async isDroppedRelatedAnime(animeId) {
        try {
            const item = await this.dropped_related_anime.get(animeId);
            return !!item;
        } catch (error) {
            console.error('Error checking dropped-related anime:', error);
            return false;
        }
    }

    // ========== REMOVED ANIME METHODS ==========

    /**
     * Add anime to removed list (user-removed from Related Anime tab)
     * @param {number} animeId Anime ID
     * @returns {Promise<boolean>} Success status
     */
    async addRemovedAnimeToDb(animeId) {
        try {
            const existing = await this.removed_anime.get(animeId);
            if (!existing) {
                await this.removed_anime.put({
                    anime_id: animeId,
                    removed_at: Date.now()
                });
            }
            return true;
        } catch (error) {
            console.error('Error adding removed anime:', error);
            return false;
        }
    }

    /**
     * Batch add multiple anime to removed list
     * @param {Array<number>} animeIds Array of anime IDs
     * @returns {Promise<boolean>} Success status
     */
    async batchAddRemovedAnime(animeIds) {
        try {
            const data = animeIds.map(id => ({
                anime_id: id,
                removed_at: Date.now()
            }));
            await this.removed_anime.bulkPut(data);
            return true;
        } catch (error) {
            console.error('Error batch adding removed anime:', error);
            return false;
        }
    }

    /**
     * Get all removed anime IDs
     * @returns {Promise<Set<number>>} Set of anime IDs
     */
    async getRemovedAnimeIdsFromDb() {
        try {
            const items = await this.removed_anime.toArray();
            return new Set(items.map(item => item.anime_id));
        } catch (error) {
            console.error('Error getting removed anime IDs:', error);
            return new Set();
        }
    }

    /**
     * Remove anime from removed list (restore it)
     * @param {number} animeId Anime ID
     * @returns {Promise<boolean>} Success status
     */
    async removeFromRemovedAnime(animeId) {
        try {
            await this.removed_anime.delete(animeId);
            return true;
        } catch (error) {
            console.error('Error removing from removed anime list:', error);
            return false;
        }
    }

    /**
     * Check if anime is in removed list
     * @param {number} animeId Anime ID
     * @returns {Promise<boolean>} True if removed
     */
    async isAnimeRemovedFromDb(animeId) {
        try {
            const item = await this.removed_anime.get(animeId);
            return !!item;
        } catch (error) {
            console.error('Error checking removed anime:', error);
            return false;
        }
    }

    /**
     * Get count of removed anime
     * @returns {Promise<number>} Count
     */
    async getRemovedAnimeCount() {
        try {
            return await this.removed_anime.count();
        } catch (error) {
            console.error('Error getting removed anime count:', error);
            return 0;
        }
    }

    /**
     * Clear all removed anime (for reset)
     * @returns {Promise<boolean>} Success status
     */
    async clearAllRemovedAnime() {
        try {
            await this.removed_anime.clear();
            return true;
        } catch (error) {
            console.error('Error clearing removed anime:', error);
            return false;
        }
    }

    // Dummy method for compatibility (no need to save with IndexedDB)
    saveDatabase() {
        // IndexedDB auto-persists, this is just for API compatibility
        console.log('ðŸ’¾ Database auto-saved by IndexedDB');
    }
}

// Export singleton instance
const databaseService = new DatabaseService();
export default databaseService;
